import React, { useCallback, useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import GameSeat from "../../components/GameSeat";
import { leaveSeat, moveItemToSlot, moveItemToInventory, removeItem, assignNote, claimMapReward, enterMapLocation } from "../../services/JubenshaService";
import { useRoomRealtime } from "../../hooks/UseRoomRealtime";
import JubenshaItemCard from "./components/JubenshaItemCard";
import JubenshaItemModal from "./components/JubenshaItemModal";
import JubenshaMap from "./components/JubenshaMap";
import "./JubenshaSeat.css";

export default function JubenshaSeat() {
  const { room_id, seat_index } = useParams();
  const history = useHistory();
  const parsedSeatIndex = Number.parseInt(seat_index, 10);
  const seatIndexNum = Number.isNaN(parsedSeatIndex) ? null : parsedSeatIndex;
  const seatIconPlaceholder = "https://placehold.co/48x48?text=ROLE";

  const handleSeatAvatarError = (e) => {
    // Prevent infinite loops if placeholder also fails
    if (e?.currentTarget?.dataset?.fallbackApplied) return;
    if (e?.currentTarget) {
      e.currentTarget.dataset.fallbackApplied = "1";
      e.currentTarget.src = seatIconPlaceholder;
    }
  };

  const handleSeatOccupied = useCallback(() => {
    alert("This seat is already open in another tab or device.");
    history.push(`/games/jubensha/${room_id}`);
  }, [history, room_id]);

  const { room, loading, error, seatPresence } = useRoomRealtime(room_id, {
    seatIndex: seatIndexNum,
    onSeatOccupied: handleSeatOccupied,
  });
  const [activeItem, setActiveItem] = useState(null);
  const [activeItemSource, setActiveItemSource] = useState(null); // { type: 'inventory' | 'slot', slotId: string | null }
  const [endingModalOpen, setEndingModalOpen] = useState(false);
  const [selectedEnding, setSelectedEnding] = useState(null);
  const [lastSeenEndingId, setLastSeenEndingId] = useState(null);
  const [seenTimelineIds, setSeenTimelineIds] = useState(new Set());
  const [seenBioIds, setSeenBioIds] = useState(new Set());
  const [seenItemIds, setSeenItemIds] = useState(new Set());
  const [seenNoteIds, setSeenNoteIds] = useState(new Set());
  const [previousStats, setPreviousStats] = useState(null);
  const [previousAttributes, setPreviousAttributes] = useState(null);
  const [newContentModal, setNewContentModal] = useState(null); // { type: 'timeline' | 'bio' | 'item' | 'note' | 'stat' | 'attribute', content: {...} }
  const [seenHydrated, setSeenHydrated] = useState(false);
  const [, setItemActionMenu] = useState(null); // { itemId, fromSlot, x, y }
  const [slotSelectModal, setSlotSelectModal] = useState(null); // { itemId }
  const [shareNoteModal, setShareNoteModal] = useState(null); // { note: {...} }

  const handleLeaveSeat = async () => {
    if (seatIndexNum === null) return;
    try {
      await leaveSeat(room_id, seatIndexNum);
      history.push(`/games/jubensha/${room_id}`);
    } catch (err) {
      console.error("Failed to leave seat", err);
      alert("Unable to leave seat right now.");
    }
  };

  // Watch for ending selection
  useEffect(() => {
    const selectedEndingId = room?.data?.selectedEndingId;
    const endings = room?.data?.endings || room?.data?.template?.endings || [];
    
    if (selectedEndingId && selectedEndingId !== lastSeenEndingId) {
      const ending = endings.find((e) => e.id === selectedEndingId);
      if (ending) {
        setSelectedEnding(ending);
        setEndingModalOpen(true);
        setLastSeenEndingId(selectedEndingId);
      }
    }
  }, [
    room?.data?.selectedEndingId,
    room?.data?.endings,
    room?.data?.template?.endings,
    lastSeenEndingId,
  ]);

  const timeline = room?.data?.timeline || [];
  const stageIndex = room?.data?.stageIndex ?? -1;
  const currentStage = timeline[stageIndex] || {};
  const stageKey = currentStage?.key || room?.data?.stage || "briefing";
  const stageIndexByKey = timeline.reduce((map, stage, idx) => {
    map[stage.key] = idx;
    return map;
  }, {});

  const seatData = room?.data?.seats?.[seatIndexNum];

  const seenStorageKey =
    seatIndexNum === null ? null : `jubensha_seen_v1:${room_id}:seat:${seatIndexNum}`;

  // Hydrate "seen" state so refresh doesn't re-pop old modals
  useEffect(() => {
    if (!seenStorageKey) return;
    if (seenHydrated) return;

    try {
      const raw = localStorage.getItem(seenStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);

      if (parsed?.seenTimelineIds) setSeenTimelineIds(new Set(parsed.seenTimelineIds));
      if (parsed?.seenBioIds) setSeenBioIds(new Set(parsed.seenBioIds));
      if (parsed?.seenItemIds) setSeenItemIds(new Set(parsed.seenItemIds));
      if (parsed?.seenNoteIds) setSeenNoteIds(new Set(parsed.seenNoteIds));
      if (parsed?.previousStats) setPreviousStats(parsed.previousStats);
      if (parsed?.previousAttributes) setPreviousAttributes(parsed.previousAttributes);
      if (parsed?.lastSeenEndingId !== undefined) setLastSeenEndingId(parsed.lastSeenEndingId);

      setSeenHydrated(true);
    } catch (e) {
      // ignore malformed storage
      setSeenHydrated(true);
    }
  }, [seenStorageKey, seenHydrated]);

  // If no saved state yet, initialize "seen" from current snapshot (no initial popups)
  useEffect(() => {
    if (!seenStorageKey) return;
    if (seenHydrated) return;
    if (!seatData || !room?.data) return;

    const timeline = room?.data?.timeline || [];
    const stageIndex = room?.data?.stageIndex ?? -1;
    const stageIndexByKey = timeline.reduce((map, stage, idx) => {
      map[stage.key] = idx;
      return map;
    }, {});

    const initialTimelineSeen = new Set(
      (seatData?.timeline || [])
        .filter((step) => {
          const idx = stageIndexByKey[step.stageKey] ?? 0;
          return idx <= stageIndex;
        })
        .map((step) => `${step.stageKey}-${step.title}`)
    );

    const initialBioSeen = new Set(
      (Array.isArray(seatData?.bio) ? seatData.bio : [])
        .filter((entry) => {
          if (!entry) return false;
          if (!entry.stageKey) return true;
          const unlockIdx = stageIndexByKey[entry.stageKey];
          return unlockIdx === undefined ? true : stageIndex >= unlockIdx;
        })
        .map((entry) => (entry.stageKey ? `bio-${entry.stageKey}-${seatIndexNum}` : `bio-base-${seatIndexNum}`))
    );

    const initialItemSeen = new Set(
      (seatData?.inventory || [])
        .map((it) => (typeof it === 'string' ? it : it?.id))
        .filter(Boolean)
    );

    const seatPublicNotes = seatData?.publicNotes || seatData?.notes?.public || [];
    const allPublicNotes = (seatPublicNotes && seatPublicNotes.length > 0)
      ? seatPublicNotes
      : (room?.data?.publicNotes || []);
    const seatPrivateNotes = seatData?.privateNotes || seatData?.notes?.private || [];
    const initialNoteSeen = new Set(
      [...(allPublicNotes || []), ...(seatPrivateNotes || [])]
        .map((n) => n?.id)
        .filter(Boolean)
    );

    setSeenTimelineIds(initialTimelineSeen);
    setSeenBioIds(initialBioSeen);
    setSeenItemIds(initialItemSeen);
    setSeenNoteIds(initialNoteSeen);
    setPreviousStats(seatData?.stats ? { ...seatData.stats } : {});
    setPreviousAttributes(seatData?.attributes ? { ...seatData.attributes } : {});
    setLastSeenEndingId(room?.data?.selectedEndingId || null);
    setSeenHydrated(true);
  }, [seenStorageKey, seenHydrated, seatData, room?.data, seatIndexNum]);

  // Persist "seen" state
  useEffect(() => {
    if (!seenStorageKey) return;
    if (!seenHydrated) return;
    try {
      localStorage.setItem(seenStorageKey, JSON.stringify({
        seenTimelineIds: Array.from(seenTimelineIds),
        seenBioIds: Array.from(seenBioIds),
        seenItemIds: Array.from(seenItemIds),
        seenNoteIds: Array.from(seenNoteIds),
        previousStats,
        previousAttributes,
        lastSeenEndingId,
        updatedAt: new Date().toISOString(),
      }));
    } catch (e) {
      // ignore quota errors
    }
  }, [seenStorageKey, seenHydrated, seenTimelineIds, seenBioIds, seenItemIds, seenNoteIds, previousStats, previousAttributes, lastSeenEndingId]);

  // Watch for new timeline, bio, items, and notes
  useEffect(() => {
    if (!seatData || !room || newContentModal || !seenHydrated) return; // Don't check if modal is already open
    if ((room?.data?.stageIndex ?? -1) < 0 || room?.status === "waiting") return;
    
    const timeline = room?.data?.timeline || [];
    const stageIndex = room?.data?.stageIndex ?? -1;
    const stageIndexByKey = timeline.reduce((map, stage, idx) => {
      map[stage.key] = idx;
      return map;
    }, {});

    // Check for new timeline entries
    const seatTimeline = seatData?.timeline || [];
    const newTimelineEntries = seatTimeline.filter((step) => {
      const idx = stageIndexByKey[step.stageKey] ?? 0;
      if (idx > stageIndex) return false; // Not unlocked yet
      const entryId = `${step.stageKey}-${step.title}`;
      return !seenTimelineIds.has(entryId);
    });

    // Check for new bio entries
    const seatBio = Array.isArray(seatData?.bio) ? seatData.bio : [];
    const newBioEntries = seatBio.filter((entry) => {
      if (!entry) return false;
      if (!entry.stageKey) {
        // Base bio (no stageKey) - only show once
        const entryId = `bio-base-${seatIndexNum}`;
        return !seenBioIds.has(entryId);
      }
      const unlockIdx = stageIndexByKey[entry.stageKey];
      if (unlockIdx === undefined || unlockIdx > stageIndex) return false; // Not unlocked yet
      const entryId = `bio-${entry.stageKey}-${seatIndexNum}`;
      return !seenBioIds.has(entryId);
    });

    // Check for new items
    const seatInventory = seatData?.inventory || [];
    const availableItems = room?.data?.availableItems || room?.data?.items || [];
    const itemsById = availableItems.reduce((acc, item) => {
      if (item?.id) acc[item.id] = item;
      return acc;
    }, {});
    const newItems = seatInventory.filter((item) => {
      const itemId = typeof item === 'string' ? item : item?.id;
      if (!itemId) return false;
      return !seenItemIds.has(itemId);
    });

    // Check for new notes (both public and private)
    const seatPublicNotes = seatData?.publicNotes || seatData?.notes?.public || [];
    const allPublicNotes = (seatPublicNotes && seatPublicNotes.length > 0)
      ? seatPublicNotes
      : (room?.data?.publicNotes || []);
    const seatPrivateNotes = seatData?.privateNotes || seatData?.notes?.private || [];
    const allNotes = [...allPublicNotes, ...seatPrivateNotes];
    const availableNotes = room?.data?.availableNotes || room?.data?.notes || [];
    const notesById = availableNotes.reduce((acc, note) => {
      if (note?.id) acc[note.id] = note;
      return acc;
    }, {});
    const newNotes = allNotes.filter((note) => {
      const noteId = note?.id;
      if (!noteId) return false;
      return !seenNoteIds.has(noteId);
    });

    // Check for stats/attributes changes
    const currentStats = seatData?.stats || {};
    const currentAttributes = seatData?.attributes || {};
    
    let statChanges = [];
    let attributeChanges = [];
    
    if (previousStats !== null) {
      // Check for stat changes
      Object.keys(currentStats).forEach((key) => {
        const oldValue = previousStats[key];
        const newValue = currentStats[key];
        if (oldValue !== undefined && oldValue !== newValue) {
          statChanges.push({ key, oldValue, newValue });
        }
      });
      // Check for new stats
      Object.keys(currentStats).forEach((key) => {
        if (previousStats[key] === undefined && currentStats[key] !== undefined) {
          statChanges.push({ key, oldValue: null, newValue: currentStats[key] });
        }
      });
    }
    
    if (previousAttributes !== null) {
      // Check for attribute changes
      Object.keys(currentAttributes).forEach((key) => {
        const oldValue = previousAttributes[key];
        const newValue = currentAttributes[key];
        if (oldValue !== undefined && oldValue !== newValue) {
          attributeChanges.push({ key, oldValue, newValue });
        }
      });
      // Check for new attributes
      Object.keys(currentAttributes).forEach((key) => {
        if (previousAttributes[key] === undefined && currentAttributes[key] !== undefined) {
          attributeChanges.push({ key, oldValue: null, newValue: currentAttributes[key] });
        }
      });
    }

    // Initialize previous values on first load (don't show modal for initial values)
    if (previousStats === null && Object.keys(currentStats).length > 0) {
      setPreviousStats({ ...currentStats });
    }
    if (previousAttributes === null && Object.keys(currentAttributes).length > 0) {
      setPreviousAttributes({ ...currentAttributes });
    }

    // Priority: timeline > bio > stats > attributes > items > notes
    if (newTimelineEntries.length > 0) {
      const entry = newTimelineEntries[0];
      const entryId = `${entry.stageKey}-${entry.title}`;
      setSeenTimelineIds(prev => new Set([...prev, entryId]));
      setNewContentModal({ type: 'timeline', content: entry });
    } else if (newBioEntries.length > 0) {
      const entry = newBioEntries[0];
      const entryId = entry.stageKey ? `bio-${entry.stageKey}-${seatIndexNum}` : `bio-base-${seatIndexNum}`;
      setSeenBioIds(prev => new Set([...prev, entryId]));
      setNewContentModal({ type: 'bio', content: entry });
    } else if (statChanges.length > 0) {
      // Update previous stats after detecting changes
      setPreviousStats({ ...currentStats });
      setNewContentModal({ type: 'stat', content: statChanges });
    } else if (attributeChanges.length > 0) {
      // Update previous attributes after detecting changes
      setPreviousAttributes({ ...currentAttributes });
      setNewContentModal({ type: 'attribute', content: attributeChanges });
    } else if (newItems.length > 0) {
      const item = newItems[0];
      const itemId = typeof item === 'string' ? item : item?.id;
      const itemData = itemsById[itemId] || (typeof item === 'object' ? item : { id: itemId, title: itemId });
      setSeenItemIds(prev => new Set([...prev, itemId]));
      setNewContentModal({ type: 'item', content: itemData });
    } else if (newNotes.length > 0) {
      const note = newNotes[0];
      const noteId = note?.id;
      const noteData = notesById[noteId] || note;
      setSeenNoteIds(prev => new Set([...prev, noteId]));
      setNewContentModal({ type: 'note', content: noteData });
    } else if (previousStats !== null && JSON.stringify(previousStats) !== JSON.stringify(currentStats)) {
      // Update stats if no modal is showing (catch any missed changes)
      setPreviousStats({ ...currentStats });
    } else if (previousAttributes !== null && JSON.stringify(previousAttributes) !== JSON.stringify(currentAttributes)) {
      // Update attributes if no modal is showing (catch any missed changes)
      setPreviousAttributes({ ...currentAttributes });
    }
  }, [
    room?.data?.stageIndex,
    seatData,
    seenHydrated,
    seenTimelineIds,
    seenBioIds,
    seenItemIds,
    seenNoteIds,
    previousStats,
    previousAttributes,
    newContentModal,
    seatIndexNum,
    room,
  ]);
  const visibleTimeline = (seatData?.timeline || []).filter((step) => {
    const idx = stageIndexByKey[step.stageKey] ?? 0;
    return idx <= stageIndex;
  });

  const unlockedBio = Array.isArray(seatData?.bio)
    ? (seatData?.bio || []).filter((entry) => {
        if (!entry) return false;
        if (!entry.stageKey) return true;
        const unlockIdx = stageIndexByKey[entry.stageKey];
        return unlockIdx === undefined ? true : stageIndex >= unlockIdx;
      })
    : [];

  const seatPublicNotes = seatData?.publicNotes || seatData?.notes?.public || [];
  const publicNotes = (seatPublicNotes && seatPublicNotes.length > 0)
    ? seatPublicNotes
    : (room?.data?.publicNotes || []);

  const seatPrivateNotes = seatData?.privateNotes || seatData?.notes?.private || [];
  const privateNotes = seatPrivateNotes;

  const connectionStatus = seatPresence?.includes(seatIndexNum);
  const maxInventory = room?.data?.maxInventory || 0;
  const availableItems = room?.data?.availableItems || room?.data?.items || [];
  const availableNotes = room?.data?.availableNotes || room?.data?.notes || [];
  const availableSharedTags = room?.data?.sharedTags || room?.data?.template?.sharedTags || [];
  const stageMapEntry =
    room?.data?.map?.[stageKey] ||
    room?.data?.template?.map?.[stageKey] ||
    null;
  const itemsById = availableItems.reduce((acc, item) => {
    if (item?.id) acc[item.id] = item;
    return acc;
  }, {});
  const tagsById = availableSharedTags.reduce((acc, tag) => {
    if (tag?.id) acc[tag.id] = tag;
    return acc;
  }, {});
  const availableSlots = room?.data?.availableSlots || room?.data?.slots || [];
  const slotDefsById = availableSlots.reduce((acc, slot) => {
    acc[slot.id] = slot;
    return acc;
  }, {});

  const normalizeItem = (entry) => {
    if (!entry) return entry;
    const base = typeof entry === 'string' ? { id: entry } : entry;
    if (base?.id && itemsById[base.id]) {
      return { ...itemsById[base.id], ...base };
    }
    return base;
  };

  const handleMoveToSlot = async (itemId, slotId = null) => {
    if (seatIndexNum === null) return;
    // If slotId is provided, move directly; otherwise show slot selection
    if (slotId) {
      try {
        await moveItemToSlot(room_id, seatIndexNum, itemId, slotId);
        setSlotSelectModal(null);
        setItemActionMenu(null);
        setActiveItem(null);
        setActiveItemSource(null);
        // Room will update automatically via socket
      } catch (err) {
        console.error("Failed to move item to slot", err);
        alert(err.response?.data?.message || "Failed to move item");
      }
    } else {
      // Show slot selection modal (item modal stays open in background)
      setSlotSelectModal({ itemId });
    }
  };

  const handleMoveToInventory = async (slotId) => {
    if (seatIndexNum === null) return;
    try {
      await moveItemToInventory(room_id, seatIndexNum, slotId);
      setItemActionMenu(null);
      setActiveItem(null);
      setActiveItemSource(null);
      // Room will update automatically via socket
    } catch (err) {
      console.error("Failed to move item to inventory", err);
      alert(err.response?.data?.message || "Failed to move item");
    }
  };

  const handleRemoveItem = async (itemId, fromSlot = null) => {
    if (seatIndexNum === null) return;
    // Confirmation is handled in the modal
    try {
      await removeItem(room_id, seatIndexNum, itemId, fromSlot);
      setItemActionMenu(null);
      setActiveItem(null);
      setActiveItemSource(null);
      // Room will update automatically via socket
    } catch (err) {
      console.error("Failed to remove item", err);
      alert(err.response?.data?.message || "Failed to remove item");
    }
  };

  const handlePublishNote = async (noteId) => {
    if (seatIndexNum === null) return;
    try {
      await assignNote(room_id, noteId, 'public');
      setShareNoteModal(null);
      // Room will update automatically via socket
    } catch (err) {
      console.error("Failed to publish note", err);
      alert(err.response?.data?.message || "Failed to publish note");
    }
  };

  const handleShareNoteToPlayer = async (noteId, targetSeatIndex) => {
    if (seatIndexNum === null || targetSeatIndex === null) return;
    try {
      await assignNote(room_id, noteId, 'private', targetSeatIndex);
      setShareNoteModal(null);
      // Room will update automatically via socket
    } catch (err) {
      console.error("Failed to share note", err);
      alert(err.response?.data?.message || "Failed to share note");
    }
  };

  return (
    <GameSeat
      room={room}
      loading={loading}
      error={error}
      seatIndex={seatIndexNum}
      onBack={() => history.push(`/games/jubensha/${room_id}`)}
    >
      {(roomData, seat) => {
        const attributeEntries = Object.entries(seat?.attributes || {});
        const radarSize = 260;
        const radarRadius = 90;
        const center = radarSize / 2;
        const radarPoints = attributeEntries.map(([key, value], idx) => {
          const angle = (2 * Math.PI * idx) / attributeEntries.length - Math.PI / 2;
          const ratio = Math.max(0, Math.min(1, Number(value) / 10));
          const x = center + radarRadius * ratio * Math.cos(angle);
          const y = center + radarRadius * ratio * Math.sin(angle);
          return `${x},${y}`;
        }).join(' ');

        const storyStarted = (room?.data?.stageIndex ?? -1) >= 0 && room?.status !== "waiting";

        return (
        <div className="jubensha-seat">
          {/* Other Players Status Section */}


          <div className="seat-header">
            <div className="seat-header-main">
              <div className="seat-story">{room.data?.templateTitle} · {room.data?.setting?.vibe}</div>
              <div className="seat-character">{seat.characterName}</div>
            </div>
            <div className="header-actions">
              <div className={`connection ${connectionStatus ? "online" : "offline"}`}
                title={connectionStatus ? "Connected" : "Offline"}
              >
                <span className="dot" />
                <span className="connection-label">{connectionStatus ? "Connected" : "Offline"}</span>
              </div>
              <button className="btn-danger btn-compact btn-leave-seat-text" onClick={handleLeaveSeat} title="离开座位">
                Leave Seat
              </button>
            </div>
          </div>

          {(room?.data?.stageIndex ?? -1) >= 0 && room?.status !== "waiting" && (
            <div className="panel panel-map">
              <JubenshaMap
                title="Game Map"
                subtitle={`Map · ${currentStage.title || stageKey}`}
                mapEntry={stageMapEntry}
                availableItems={availableItems}
                availableNotes={availableNotes}
                availableTags={availableSharedTags}
                variant="room-acquire"
                stageKey={stageKey}
                seatPosition={seatIndexNum}
                seatData={seat}
                onClaimReward={(payload) => claimMapReward(room_id, payload)}
                onEnterLocation={(payload) => enterMapLocation(room_id, payload)}
              />
            </div>
          )}

          {activeItem && (
            <JubenshaItemModal 
              item={activeItem} 
              onClose={() => {
                setActiveItem(null);
                setActiveItemSource(null);
              }}
              sourceType={activeItemSource?.type || null}
              slotId={activeItemSource?.slotId || null}
              onMoveToSlot={handleMoveToSlot}
              onMoveToInventory={handleMoveToInventory}
              onRemove={handleRemoveItem}
              availableSlots={availableSlots}
            />
          )}

          {endingModalOpen && selectedEnding && (
            <div className="ending-modal-overlay">
              <div className="ending-modal">
                <div className="ending-modal-head">
                  <div className="ending-modal-title">{selectedEnding.title}</div>
                  <button
                    className="ending-modal-close"
                    onClick={() => setEndingModalOpen(false)}
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&#10005;</span>
                  </button>
                </div>
                <div className="ending-modal-body">
                  <div className="ending-modal-description">{selectedEnding.description}</div>
                </div>
                <div className="ending-modal-actions">
                  <button
                    className="btn-primary"
                    type="button"
                    onClick={() => setEndingModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {newContentModal && (
            <div className="new-content-modal-overlay">
              <div className="new-content-modal">
                <div className="new-content-modal-head">
                  <div className="new-content-modal-title">
                    {newContentModal.type === 'timeline' ? '📜 新时间线' : 
                     newContentModal.type === 'bio' ? '📖 新背景' :
                     newContentModal.type === 'item' ? '🎒 New Item' :
                     newContentModal.type === 'note' ? '📝 New Note' :
                     newContentModal.type === 'stat' ? '📊 数值更新' :
                     '⚡ 属性更新'}
                  </div>
                  <button
                    className="new-content-modal-close"
                    onClick={() => setNewContentModal(null)}
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&#10005;</span>
                  </button>
                </div>
                <div className="new-content-modal-body">
                  {newContentModal.type === 'timeline' ? (
                    <>
                      <div className="new-content-stage">
                        {newContentModal.content.stageKey ? `阶段: ${newContentModal.content.stageKey}` : ''}
                      </div>
                      <div className="new-content-title">{newContentModal.content.title}</div>
                      <div className="new-content-text">{newContentModal.content.text}</div>
                    </>
                  ) : newContentModal.type === 'bio' ? (
                    <>
                      <div className="new-content-stage">
                        {newContentModal.content.stageKey ? `Unlocked Stage: ${newContentModal.content.stageKey}` : 'Base Bio'}
                      </div>
                      <div className="new-content-text">{newContentModal.content.content}</div>
                    </>
                  ) : newContentModal.type === 'item' ? (
                    <>
                      {newContentModal.content.iconUrl && (
                        <img 
                          src={newContentModal.content.iconUrl} 
                          alt={newContentModal.content.title || 'Item'}
                          className="new-content-icon"
                        />
                      )}
                      <div className="new-content-title">{newContentModal.content.title || newContentModal.content.name || newContentModal.content.id}</div>
                      <div className="new-content-text">{newContentModal.content.description || newContentModal.content.note || '暂无描述'}</div>
                    </>
                  ) : newContentModal.type === 'note' ? (
                    <>
                      <div className="new-content-title">{newContentModal.content.title || newContentModal.content.name || 'Note'}</div>
                      <div className="new-content-text">{newContentModal.content.message || newContentModal.content.description || 'No content'}</div>
                    </>
                  ) : newContentModal.type === 'stat' ? (
                    <>
                      <div className="new-content-title">Stat Changes</div>
                      <div className="stat-changes-list">
                        {newContentModal.content.map((change, idx) => (
                          <div key={idx} className="stat-change-item">
                            <div className="stat-change-label">{change.key}</div>
                            <div className="stat-change-values">
                              <span className="stat-change-old">{change.oldValue === null ? '—' : change.oldValue}</span>
                              <span className="stat-change-arrow">→</span>
                              <span className="stat-change-new">{change.newValue}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="new-content-title">Attribute Changes</div>
                      <div className="stat-changes-list">
                        {newContentModal.content.map((change, idx) => (
                          <div key={idx} className="stat-change-item">
                            <div className="stat-change-label">{change.key}</div>
                            <div className="stat-change-values">
                              <span className="stat-change-old">{change.oldValue === null ? '—' : change.oldValue}</span>
                              <span className="stat-change-arrow">→</span>
                              <span className="stat-change-new">{change.newValue}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="new-content-modal-actions">
                  <button
                    className="btn-primary"
                    type="button"
                    onClick={() => {
                      setNewContentModal(null);
                      // Trigger re-check for next entry after a brief delay
                      setTimeout(() => {}, 100);
                    }}
                  >
                    Read
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="panel-grid">
            <div className="panel panel-stats" style={{ minHeight: 0 }}>
              <div className="panel-title">Stats</div>
              <div className="stats-grid">
                {Object.entries(seat.stats || {}).map(([key, value]) => {
                  // Assume maxValue is 10 for all stats, or you can replace with seat.maxStats[key] if available
                  const maxValue = seat.maxStats?.[key] ?? 10;
                  return (
                    <div key={key} className="stat-card stat-card-compact">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="stat-label">{key}</span>
                        <span className="stat-value inline">{value}/{maxValue}</span>
                      </div>
                      <div style={{ marginTop: '6px' }}>
                        <div className="stat-bar">
                          <div className="stat-bar-fill" style={{ width: `${Math.min(100, Math.max(0, (Number(value) / maxValue) * 100))}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="panel panel-timeline" style={{ maxHeight: '320px', overflowY: 'auto' }}>
              <div className="panel-title">Your Timeline</div>
              <div className="timeline-list timeline-scroll">
                {visibleTimeline.map((step, idx) => (
                  <div key={`${step.stageKey}-${idx}`} className="timeline-item">
                    <div className="timeline-meta">{step.title}</div>
                    <div className="timeline-stage">Stage: {step.stageKey}</div>
                    <div className="timeline-text">{step.text}</div>
                  </div>
                ))}
                {visibleTimeline.length === 0 && <div className="timeline-empty">Waiting for host to start.</div>}
              </div>
            </div>
          </div>

          <div className="panel-grid">
            <div className="panel panel-attributes">
              <div className="panel-title">Attributes</div>
              {attributeEntries.length > 2 && (
                <div className="radar-card">
                  <svg width={radarSize} height={radarSize} viewBox={`0 0 ${radarSize} ${radarSize}`}>
                    <circle cx={center} cy={center} r={radarRadius} className="radar-ring" />
                    <circle cx={center} cy={center} r={radarRadius * 0.66} className="radar-ring" />
                    <circle cx={center} cy={center} r={radarRadius * 0.33} className="radar-ring" />
                    <polygon points={radarPoints} className="radar-area" />
                    {attributeEntries.map(([key], idx) => {
                      const angle = (2 * Math.PI * idx) / attributeEntries.length - Math.PI / 2;
                      const labelX = center + (radarRadius + 16) * Math.cos(angle);
                      const labelY = center + (radarRadius + 16) * Math.sin(angle);
                      return (
                        <text key={key} x={labelX} y={labelY} className="radar-label" textAnchor="middle" dominantBaseline="middle">
                          {key}
                        </text>
                      );
                    })}
                  </svg>
                </div>
              )}
              <div className="attr-grid">
                {Object.entries(seat.attributes || {}).map(([key, value]) => (
                  <div key={key} className="attribute-card">
                    <div className="attribute-label">{key}</div>
                    <div className="attribute-value">{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {storyStarted && (
              <div className="panel panel-bio">
                <div className="panel-title">Bio</div>
                {Array.isArray(seat.bio) ? (
                  <div className="bio-list bio-scroll">
                    {unlockedBio.map((entry, idx) => (
                      <div key={`${entry.stageKey || 'origin'}-${idx}`} className="bio-entry">
                        <div className="bio-stage">{entry.stageKey ? `Unlocked Stage · ${entry.stageKey}` : 'Base Bio'}</div>
                        <div className="bio-text">{entry.content}</div>
                      </div>
                    ))}
                    {unlockedBio.length === 0 && <div className="timeline-empty">Waiting for more bio to unlock.</div>}
                  </div>
                ) : (
                  <p className="panel-body scrollable-bio bio-scroll">{seat.bio}</p>
                )}
              </div>
            )}
          </div>

          <div className="panel panel-tags">
            <div className="panel-title">Tags</div>
            <div className="seat-tags">
              {(seat.tags || []).map((t, idx) => {
                const tagId = typeof t === "string" ? t : t?.id;
                const full = (tagId && tagsById[tagId])
                  ? tagsById[tagId]
                  : (typeof t === "object" ? t : { id: tagId, title: tagId });
                const icon = full?.iconUrl;
                const title = full?.title || full?.name || full?.id || `tag-${idx}`;
                const desc = full?.description || "";
                return (
                  <div key={full?.id || tagId || idx} className="seat-tag-pill" title={desc || title}>
                    {icon ? <img className="seat-tag-icon" src={icon} alt={title} /> : <span className="seat-tag-fallback">🏷️</span>}
                    <span className="seat-tag-title">{title}</span>
                  </div>
                );
              })}
              {(!seat.tags || seat.tags.length === 0) && <div className="timeline-empty">No tags yet.</div>}
            </div>
          </div>


          {/* Removed duplicate timeline section */}

          <div className="panel">
            <div className="panel-title">Inventory</div>
            {maxInventory > 0 && (
              <div className="inventory-capacity">{(seat.inventory || []).length} / {maxInventory}</div>
            )}
            <div className="inventory-grid inventory-grid-cards">
              {(seat.inventory || []).map((item, idx) => {
                const normalized = normalizeItem(item);
                const itemId = normalized?.id;
                return (
                  <div key={normalized?.id || idx} className="inventory-item-wrapper">
                    <JubenshaItemCard
                      item={normalized}
                      showDescription={false}
                      compact
                      onInspect={() => {
                        setActiveItem(normalized);
                        setActiveItemSource({ type: 'inventory', slotId: null });
                      }}
                    />
                    <div className="item-actions">
                      <button
                        className="item-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSlotSelectModal({ itemId });
                        }}
                        title="Move to Slot"
                      >
                        ⚙️
                      </button>
                      <button
                        className="item-action-btn item-action-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveItem(itemId);
                        }}
                        title="Discard Item"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })}
              {(!seat.inventory || seat.inventory.length === 0) && <div className="timeline-empty">No items yet.</div>}
            </div>
          </div>

          <div className="panel">
            <div className="panel-title">Slots</div>
            <div className="inventory-grid inventory-grid-cards">
              {Object.entries(seat.slots || {}).map(([slot, value]) => {
                const normalized = normalizeItem(value);
                const slotDef = slotDefsById[slot];
                const slotLabel = slotDef?.title || slot;
                const cardDescription = normalized?.description || normalized?.note || slotDef?.description || (normalized ? '' : '空');
                const displayItem = normalized
                  ? { ...normalized, subtitle: cardDescription, slotLabel }
                  : { id: 'empty', title: 'Empty', subtitle: cardDescription, slotLabel };
                const itemId = normalized?.id;
                return (
                  <div key={slot} className="inventory-item-wrapper">
                    <JubenshaItemCard
                      item={displayItem}
                      showDescription={false}
                      compact
                      onInspect={() => {
                        if (normalized) {
                          setActiveItem(displayItem);
                          setActiveItemSource({ type: 'slot', slotId: slot });
                        }
                      }}
                    />
                    {normalized && (
                      <div className="item-actions">
                        <button
                          className="item-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveToInventory(slot);
                          }}
                          title="Move to Inventory"
                        >
                          📦
                        </button>
                        <button
                          className="item-action-btn item-action-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveItem(itemId, slot);
                          }}
                          title="Discard Equipment"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="panel-grid">
            <div className="panel">
              <div className="panel-title">Public Notes</div>
              <div className="notes-list">
                {publicNotes.map((note, idx) => (
                  <div key={idx} className="note-card public-note">
                    <div className="note-head">
                      <div className="note-title">{note.title}</div>
                      <div className="note-meta">{new Date(note.assignedAt || Date.now()).toLocaleString()}</div>
                    </div>
                    <div className="note-body">{note.message}</div>
                  </div>
                ))}
                {publicNotes.length === 0 && <div className="timeline-empty">No public notes</div>}
              </div>
            </div>

            <div className="panel">
              <div className="panel-title">Private Notes</div>
              <div className="notes-list">
                {privateNotes.map((note, idx) => (
                  <div key={idx} className="note-card private-note">
                    <div className="note-head">
                      <div className="note-title">{note.title}</div>
                      <div className="note-meta">
                        {new Date(note.assignedAt || Date.now()).toLocaleString()}
                        <button
                          className="note-share-btn"
                          onClick={() => setShareNoteModal({ note })}
                          title="Share or Publish Note"
                        >
                          📤
                        </button>
                      </div>
                    </div>
                    <div className="note-body">{note.message}</div>
                  </div>
                ))}
                {privateNotes.length === 0 && <div className="timeline-empty">No private notes</div>}
              </div>
            </div>
          </div>
          {slotSelectModal && (
            <div className="slot-select-modal-overlay" onClick={() => setSlotSelectModal(null)} style={{ zIndex: 60 }}>
              <div className="slot-select-modal" onClick={(e) => e.stopPropagation()}>
                <div className="slot-select-modal-head">
                  <div className="slot-select-modal-title">Select Slot</div>
                  <button
                    className="slot-select-modal-close"
                    onClick={() => setSlotSelectModal(null)}
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&#10005;</span>
                  </button>
                </div>
                <div className="slot-select-modal-body">
                  {availableSlots.map((slot) => {
                    const slotItem = seat.slots?.[slot.id];
                    const isOccupied = slotItem && (typeof slotItem === 'string' ? slotItem : slotItem?.id) !== null;
                    return (
                      <button
                        key={slot.id}
                        className={`slot-select-option ${isOccupied ? 'occupied' : ''}`}
                        onClick={() => handleMoveToSlot(slotSelectModal.itemId, slot.id)}
                        disabled={isOccupied}
                      >
                        <div className="slot-select-label">{slot.title}</div>
                        <div className="slot-select-desc">{slot.description}</div>
                        {isOccupied && <div className="slot-select-status">Equipped</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {shareNoteModal && (
            <div className="slot-select-modal-overlay" onClick={() => setShareNoteModal(null)} style={{ zIndex: 60 }}>
              <div className="slot-select-modal" onClick={(e) => e.stopPropagation()}>
                <div className="slot-select-modal-head">
                  <div className="slot-select-modal-title">Share Note: {shareNoteModal.note.title}</div>
                  <button
                    className="slot-select-modal-close"
                    onClick={() => setShareNoteModal(null)}
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&#10005;</span>
                  </button>
                </div>
                <div className="slot-select-modal-body">
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      className="btn-primary"
                      style={{ width: '100%', marginBottom: '12px' }}
                      onClick={() => handlePublishNote(shareNoteModal.note.id)}
                    >
                      📢 Publish to Everyone
                    </button>
                    <div style={{ 
                      textAlign: 'center', 
                      color: '#9fb3c8', 
                      fontSize: '13px',
                      marginBottom: '16px',
                      padding: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      Or share privately with a player:
                    </div>
                  </div>
                  {(room?.data?.seats || []).map((player, idx) => {
                    if (idx === seatIndexNum) return null; // skip self
                    return (
                      <button
                        key={idx}
                        className="slot-select-option"
                        onClick={() => handleShareNoteToPlayer(shareNoteModal.note.id, idx)}
                      >
                        <div className="slot-select-label">
                          Seat {idx + 1} · {player.characterName}
                          {player.playerName && ` (${player.playerName})`}
                        </div>
                        <div className="slot-select-desc">Send private note</div>
                      </button>
                    );
                  })}
                  {((room?.data?.seats || []).filter((_, idx) => idx !== seatIndexNum).length === 0) && (
                    <div className="slot-select-option" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                      <div className="slot-select-label">No other players available</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Other Players Status Section (bottom) */}
          <div className="other-players-status-section">
            <div className="other-players-status-list">
              {(room?.data?.seats || []).map((player, idx) => {
                if (idx === seatIndexNum) return null; // skip self
                const isConnected = seatPresence?.includes(idx);
                const templateCharacterIcon =
                  room?.data?.template?.characters?.find(
                    (c) => c?.id === player?.characterId || c?.name === player?.characterName,
                  )?.iconUrl || null;
                const playerIcon =
                  player?.iconUrl || player?.characterIcon || templateCharacterIcon || seatIconPlaceholder;
                return (
                  <div className="other-player-card" key={idx}>
                    <div className="other-player-header">
                      <img
                        className="other-player-icon"
                        src={playerIcon}
                        alt={player?.characterName || "玩家"}
                        loading="lazy"
                        onError={handleSeatAvatarError}
                      />
                      <span className="other-player-name">{player?.characterName || `玩家${idx+1}`}</span>
                      <span className={`other-player-connection ${isConnected ? 'online' : 'offline'}`}></span>
                    </div>
                    <div className="other-player-status-bars">
                      {player?.stats && Object.entries(player.stats).map(([k, v]) => {
                        const maxValue = player.maxStats?.[k] ?? 10;
                        const percent = Math.min(100, Math.max(0, (Number(v) / maxValue) * 100));
                        return (
                          <div className="other-player-status-bar" key={k}>
                            <span className="other-player-status-label">{k}</span>
                            <span className="other-player-status-value">{v}/{maxValue}</span>
                            <div className="other-player-status-bar-bg">
                              <div className="other-player-status-bar-fill" style={{ width: percent + '%' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="other-player-slots">
                      {player?.slots && Object.entries(player.slots).map(([slot, value]) => {
                        // Find the item for this slot
                        let item = value;
                        if (typeof item === 'string' && (room?.data?.items || room?.data?.availableItems)) {
                          const itemsArr = room?.data?.items || room?.data?.availableItems;
                          item = itemsArr.find(i => i.id === value) || value;
                        }
                        const normalized = typeof item === 'string' ? { id: item } : item || {};
                        const iconUrl = normalized.iconUrl || 'https://placehold.co/32x32?text=ITEM';
                        const isEmpty = !normalized.id || normalized.id === 'empty';
                        return (
                          <button
                            key={slot}
                            className="other-player-slot-image"
                            title={normalized.title || normalized.name || slot}
                            style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: isEmpty ? 'default' : 'pointer' }}
                            onClick={() => {
                              if (!isEmpty) {
                                setActiveItem(normalized);
                                setActiveItemSource(null); // Other player's item, no actions available
                              }
                            }}
                            disabled={isEmpty}
                          >
                            <img
                              src={iconUrl}
                              alt={normalized.title || normalized.name || slot}
                              style={{ width: 32, height: 32, borderRadius: 6, opacity: isEmpty ? 0.3 : 1, border: '1px solid #222', background: '#181c2a' }}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        );
      }}
    </GameSeat>
  );
}
